/**
 * Maps the network operator behind an IP (the ASN organisation) to a recognisable
 * label and a network class.
 *
 * IMPORTANT: an IP identifies the NETWORK, not the product. Many consumer VPNs
 * rent capacity from the same few hosting companies, so "DataCamp" may serve
 * several brands at once. Treat brand names as strong hints, never as proof, and
 * fall back to the raw operator name whenever there's no confident match.
 */
export type NetworkClass =
  | 'vpn'
  | 'hosting'
  | 'business'
  | 'isp'
  | 'satellite'
  | 'residential_proxy'

interface OperatorRule {
  /** Case-insensitive substring matched against the ASN organisation. */
  match: string
  label: string
  networkClass: NetworkClass
}

const OPERATOR_RULES: OperatorRule[] = [
  // Consumer VPN operators and the hosts they commonly resell.
  { match: 'nordvpn', label: 'NordVPN', networkClass: 'vpn' },
  { match: 'datacamp', label: 'DataCamp (NordVPN / others)', networkClass: 'vpn' },
  { match: 'm247', label: 'M247 (multi-brand VPN host)', networkClass: 'vpn' },
  { match: 'expressvpn', label: 'ExpressVPN', networkClass: 'vpn' },
  { match: 'surfshark', label: 'Surfshark', networkClass: 'vpn' },
  { match: 'private internet access', label: 'Private Internet Access', networkClass: 'vpn' },
  { match: 'privateinternetaccess', label: 'Private Internet Access', networkClass: 'vpn' },
  { match: 'mullvad', label: 'Mullvad', networkClass: 'vpn' },
  { match: 'proton', label: 'Proton VPN', networkClass: 'vpn' },
  { match: 'cyberghost', label: 'CyberGhost', networkClass: 'vpn' },
  { match: 'ipvanish', label: 'IPVanish', networkClass: 'vpn' },
  { match: 'windscribe', label: 'Windscribe', networkClass: 'vpn' },
  { match: 'tunnelbear', label: 'TunnelBear', networkClass: 'vpn' },
  { match: 'zenmate', label: 'ZenMate', networkClass: 'vpn' },
  { match: 'hide.me', label: 'hide.me', networkClass: 'vpn' },
  { match: 'warp', label: 'Cloudflare WARP', networkClass: 'vpn' },
  { match: 'opera', label: 'Opera VPN', networkClass: 'vpn' },

  // Cloud and hosting networks — proxies, scrapers, and corporate egress.
  { match: 'amazon', label: 'AWS', networkClass: 'hosting' },
  { match: 'google cloud', label: 'Google Cloud', networkClass: 'hosting' },
  { match: 'microsoft', label: 'Azure', networkClass: 'hosting' },
  { match: 'digitalocean', label: 'DigitalOcean', networkClass: 'hosting' },
  { match: 'hetzner', label: 'Hetzner', networkClass: 'hosting' },
  { match: 'ovh', label: 'OVH', networkClass: 'hosting' },
  { match: 'linode', label: 'Linode', networkClass: 'hosting' },
  { match: 'oracle', label: 'Oracle Cloud', networkClass: 'hosting' },
  { match: 'choopa', label: 'Vultr / Choopa', networkClass: 'hosting' },
  { match: 'vultr', label: 'Vultr', networkClass: 'hosting' },
  { match: 'leaseweb', label: 'Leaseweb', networkClass: 'hosting' },
  { match: 'contabo', label: 'Contabo', networkClass: 'hosting' },

  // Satellite operators — often the only viable route where terrestrial
  // infrastructure is degraded, filtered, or absent.
  { match: 'spacex', label: 'Starlink', networkClass: 'satellite' },
  { match: 'starlink', label: 'Starlink', networkClass: 'satellite' },
  { match: 'viasat', label: 'Viasat', networkClass: 'satellite' },
  { match: 'hughes', label: 'HughesNet', networkClass: 'satellite' },
  { match: 'ses ', label: 'SES / O3b', networkClass: 'satellite' },
  { match: 'eutelsat', label: 'Eutelsat', networkClass: 'satellite' },
  { match: 'thuraya', label: 'Thuraya', networkClass: 'satellite' },
  { match: 'inmarsat', label: 'Inmarsat', networkClass: 'satellite' },
  { match: 'iridium', label: 'Iridium', networkClass: 'satellite' },

  // Residential proxy vendors — traffic exits through rented consumer lines, so
  // geo-IP looks entirely legitimate and only the ASN reveals it.
  { match: 'bright data', label: 'Bright Data (residential proxy)', networkClass: 'residential_proxy' },
  { match: 'luminati', label: 'Bright Data / Luminati', networkClass: 'residential_proxy' },
  { match: 'oxylabs', label: 'Oxylabs (residential proxy)', networkClass: 'residential_proxy' },
  { match: 'iproyal', label: 'IPRoyal (residential proxy)', networkClass: 'residential_proxy' },
  { match: 'smartproxy', label: 'Smartproxy (residential proxy)', networkClass: 'residential_proxy' },
  { match: 'soax', label: 'SOAX (residential proxy)', networkClass: 'residential_proxy' },
  { match: 'packetstream', label: 'PacketStream', networkClass: 'residential_proxy' },
  { match: 'netnut', label: 'NetNut (residential proxy)', networkClass: 'residential_proxy' },

  // Corporate security egress — looks like a VPN because it is one.
  { match: 'zscaler', label: 'Zscaler (corporate)', networkClass: 'business' },
  { match: 'netskope', label: 'Netskope (corporate)', networkClass: 'business' },
  { match: 'palo alto', label: 'Palo Alto Prisma (corporate)', networkClass: 'business' }
]

export interface NetworkIdentity {
  /** Best-effort operator label, or the raw organisation name. */
  label: string
  networkClass: NetworkClass
}

export function identifyNetwork(organisation: string | null): NetworkIdentity | null {
  if (!organisation) return null

  const normalised = organisation.toLowerCase()
  const rule = OPERATOR_RULES.find(candidate => normalised.includes(candidate.match))

  if (rule) return { label: rule.label, networkClass: rule.networkClass }
  return { label: organisation, networkClass: 'isp' }
}
